import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { adminAPI, reviewAPI, listingAPI, listingReviewAPI } from '../lib/api';
import api from '../lib/api';
import AdminCapabilityVisibilityPanel from '../components/admin/AdminCapabilityVisibilityPanel';
import ListingReviewBatchModal from '../components/admin/ListingReviewBatchModal';
import {
  ADMIN_LISTINGS_TABLE_TEST_ID,
  adminListingsActionsCellTestId,
  adminListingsRowBg,
} from '../lib/adminListingsTableLayout';
import {
  navigateToAdminListingView,
  parseAdminTabFromSearchParams,
} from '../lib/adminListingsNavigation';
import {
  buildListingReference,
  sortListingsByOrder,
} from '../lib/adminListingReference';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Users,
  Building2,
  MessageSquare,
  Bell,
  Shield,
  Check,
  X,
  Loader2,
  TrendingUp,
  AlertCircle,
  Eye,
  Video,
  Heart,
  Bot,
  Crown,
  RefreshCw,
  Search,
  Calendar,
  UserCheck,
  UserX,
  Mail,
  MoreHorizontal,
  ChevronDown,
  PlayCircle,
  CreditCard,
  Wallet,
  Settings,
  ToggleLeft,
  ToggleRight,
  Calculator,
  MapPin,
  Plus,
  Edit2,
  Trash2,
  DollarSign,
  Send,
  Gauge,
  ClipboardCheck,
  AlertTriangle,
  Timer,
  Activity,
  Flag,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { arePaymentsEnabled } from '../lib/paymentsAvailability';
import {
  ADMIN_SENTRY_VERIFICATION_BUTTON_TEST_ID,
  ADMIN_SENTRY_VERIFICATION_CARD_TEST_ID,
  ADMIN_SENTRY_VERIFICATION_RESULT_TEST_ID,
  resolveSentryTestError,
  resolveSentryTestResult,
  resultToneClass,
} from '../lib/adminSentryVerification';
import {
  ADMIN_INQUIRY_TRACE_CARD_TEST_ID,
  ADMIN_QA_OWNER_LISTINGS_CARD_TEST_ID,
  formatTraceTimestamp,
  getRoutingHealthBadgeClass,
  getRoutingHealthLabel,
  qaOwnerMatchClass,
  qaOwnerMatchLabel,
  qaOwnerMismatchExplanation,
  safeAdminEmailRef,
  safeOwnerEmailRef,
  summarizeMismatchFlags,
} from '../lib/adminInquiryTrace';
import {
  ADMIN_INQUIRY_TRACE_FOCUSED_ROW_TEST_ID,
  adminBookingTraceBtnTestId,
  parseTraceInquiryIdFromSearchParams,
} from '../lib/adminBookingsTraceNavigation';
import {
  getInquiryRecordId,
  getInquiryStatusBadgeConfig,
  shortId,
} from '../lib/bookingInquiryDisplay';
import { translateTemplate } from '../lib/publicListingTruth';

const TABS = ['overview', 'ops', 'users', 'bookings', 'reviews', 'listings', 'contact', 'settings'];

const isGermanLocale = (locale) => String(locale || '').toLowerCase().startsWith('de');

/** EN / DE / ES fallbacks when a translation key is missing from the loaded locale bundle. */
const localized = (locale, en, de, es = en) => {
  const code = String(locale || '').toLowerCase();
  if (code.startsWith('de')) return de;
  if (code.startsWith('es')) return es;
  return en;
};

/** Expanded review panel labels — bundle merges en-US, so DE/ES need explicit copy when API has no override. */
const adminCtxLabel = (t, locale, key, en, de, es = en) => {
  const code = String(locale || '').toLowerCase();
  const localeText = localized(locale, en, de, es);
  if (code.startsWith('en')) return t(key, en);
  const bundled = t(key, localeText);
  return bundled === en ? localeText : bundled;
};

function getListingPhotoCount(listing) {
  return Number(listing.photos?.length ?? listing.photo_count ?? 0) || 0;
}

function formatPhotoCountLabel(count, locale) {
  const n = Number(count) || 0;
  if (isGermanLocale(locale)) return n === 1 ? '1 Foto' : `${n} Fotos`;
  return n === 1 ? '1 photo' : `${n} photos`;
}

// Detect a raw enum-looking value (e.g. "READY_FOR_REVIEW") so we never show it in the UI.
function looksLikeRawEnum(value) {
  return typeof value === 'string' && /^[A-Z0-9]+(?:_[A-Z0-9]+)+$/.test(value.trim());
}

function formatMonthlyPrice(amount, locale) {
  const value = Number(amount) || 0;
  const german = isGermanLocale(locale);
  const numberLocale = german ? 'de-DE' : 'en-US';
  const suffix = german ? '/Monat' : '/mo';
  let formatted;
  try {
    formatted = new Intl.NumberFormat(numberLocale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    formatted = `€${value.toLocaleString(numberLocale)}`;
  }
  // Use non-breaking spaces so the currency + monthly suffix never wraps onto two lines.
  return `${formatted}${suffix}`.replace(/\s/g, '\u00A0');
}

function formatAdminListingStatus(listing, t, locale) {
  const german = isGermanLocale(locale);
  const resolve = (key, en, de) => {
    const fallback = german ? de : en;
    const translated = t(key, fallback);
    // Guard: if translation is missing/empty or returns a raw enum value, use the explicit fallback.
    if (!translated || looksLikeRawEnum(translated)) return fallback;
    return translated;
  };

  if (listing.auto_deactivated) {
    return resolve('admin.status_auto_deactivated', 'Auto-Deactivated', 'Automatisch deaktiviert');
  }
  const statusMap = {
    READY_FOR_REVIEW: ['admin.status_ready_for_review', 'Ready for Review', 'Bereit zur Prüfung'],
    PUBLISHED: ['admin.status_published', 'Published', 'Veröffentlicht'],
    DRAFT: ['admin.status_draft', 'Draft', 'Entwurf'],
    INCOMPLETE: ['admin.status_incomplete', 'Incomplete', 'Unvollständig'],
    PAUSED: ['admin.status_paused', 'Paused', 'Pausiert'],
  };
  const entry = statusMap[listing.status];
  if (entry) return resolve(entry[0], entry[1], entry[2]);
  // Unknown status: humanize the raw value rather than displaying the enum directly.
  return (listing.status || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Compute per-listing readiness score from existing listing fields (pure, no API call)
function listingReadinessScore(listing) {
  const photoCount = listing.photos?.length ?? listing.photo_count ?? 0;
  const checks = [
    { key: 'title',       pass: (listing.title?.length ?? 0) >= 10 },
    { key: 'description', pass: (listing.description?.length ?? 0) >= 50 },
    { key: 'location',    pass: !!(listing.location_id || listing.location_name) },
    { key: 'bedrooms',    pass: (listing.bedrooms ?? 0) >= 1 },
    { key: 'price',       pass: (listing.base_amount ?? 0) > 0 },
    { key: 'photo1',      pass: photoCount >= 1 },
    { key: 'photo3',      pass: photoCount >= 3 },
    { key: 'no_action',   pass: !listing.requires_action },
    { key: 'reviewer',    pass: !!listing.assigned_reviewer_id },
    { key: 'completion',  pass: (listing.completion_percent ?? 0) >= 80 },
  ];
  const passed = checks.filter(c => c.pass).length;
  return { checks, passed, total: checks.length, pct: Math.round((passed / checks.length) * 100) };
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t, locale } = useLanguage();
  const traceCardRef = useRef(null);
  const focusedTraceInquiryId = parseTraceInquiryIdFromSearchParams(searchParams);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookingInquiries, setBookingInquiries] = useState([]);
  const [contactInquiries, setContactInquiries] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [listingStatusFilter, setListingStatusFilter] = useState('');
  const [listingSort, setListingSort] = useState('');
  const [approvingListing, setApprovingListing] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // { listingId, title }
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [reviewNotesModal, setReviewNotesModal] = useState(null); // full listing object
  const [assigningListing, setAssigningListing] = useState(null);
  const [opsSummary, setOpsSummary] = useState(null);
  const [opsLoading, setOpsLoading] = useState(false);
  const [inquiryTraces, setInquiryTraces] = useState([]);
  const [inquiryTraceLoading, setInquiryTraceLoading] = useState(false);
  const [inquiryTraceError, setInquiryTraceError] = useState(null);
  const [configuredTempOwnerEmail, setConfiguredTempOwnerEmail] = useState('');
  const [qaOwnerEmail, setQaOwnerEmail] = useState('');
  const [qaOwnerListings, setQaOwnerListings] = useState(null);
  const [qaOwnerLoading, setQaOwnerLoading] = useState(false);
  const [qaOwnerError, setQaOwnerError] = useState(null);
  const [qaCreateLoading, setQaCreateLoading] = useState(false);
  const [sentryTestLoading, setSentryTestLoading] = useState(false);
  const [sentryTestOutcome, setSentryTestOutcome] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [moderating, setModerating] = useState(null);
  const [processingReminders, setProcessingReminders] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [updatingPlanId, setUpdatingPlanId] = useState(null);
  const [activeToggleConfirm, setActiveToggleConfirm] = useState(null); // { id, email, nextActive }
  const [refundFees, setRefundFees] = useState(null);
  const [savingFees, setSavingFees] = useState(false);
  const [mapSettings, setMapSettings] = useState({ amenities_enabled: true });
  const [savingMapSettings, setSavingMapSettings] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [savingPaymentSettings, setSavingPaymentSettings] = useState(false);
  
  // TLA Rates state
  const [tlaRates, setTlaRates] = useState([]);
  const [loadingTlaRates, setLoadingTlaRates] = useState(false);
  const [savingTlaRate, setSavingTlaRate] = useState(false);
  const [editingTlaRate, setEditingTlaRate] = useState(null);
  const [newTlaRate, setNewTlaRate] = useState({
    location_key: '',
    location_name: '',
    country: '',
    daily_single: '',
    daily_family: '',
    source: 'DoD Travel Management Office',
    effective_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    const tab = parseAdminTabFromSearchParams(searchParams, TABS);
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, isAdmin, authLoading, navigate]);

  const loadPendingReviews = async () => {
    try {
      const reviewsRes = await adminAPI.getPendingReviews();
      setPendingReviews(reviewsRes.data.reviews || []);
    } catch (error) {
      console.error('Failed to load pending reviews:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, allListingsRes] = await Promise.all([
        adminAPI.getStats(),
        api.get('/admin/all-listings?limit=100'),
      ]);
      setStats(statsRes.data);
      const listings = allListingsRes.data.listings || [];
      setAllListings(listings);
      setPendingListings(listings.filter(l => l.status === 'READY_FOR_REVIEW'));
      await loadPendingReviews();
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const params = {};
      if (userSearch) params.search = userSearch;
      if (userFilter) params.role = userFilter;
      const res = await adminAPI.getUsers(params);
      setUsers(res.data.users || []);
    } catch (error) {
      setUsersError(error.response?.data?.detail || 'Failed to load users. Check your connection and try again.');
    } finally {
      setUsersLoading(false);
    }
  };

  const normalizeRole = (role) => String(role || '').toUpperCase() || 'USER';

  const handleRoleChange = async (targetUser, newRole) => {
    const roleUpper = String(newRole).toUpperCase();
    const currentRoleUpper = normalizeRole(targetUser.role);
    if (roleUpper === currentRoleUpper) return;

    if (targetUser.id === user.id) {
      toast.error('You cannot change your own role.');
      return;
    }

    if (roleUpper === 'ADMIN') {
      if (!window.confirm(`Grant ADMIN access to ${targetUser.email}?\n\nThis gives full platform control including the ability to manage other admins.`)) return;
    } else if (currentRoleUpper === 'ADMIN') {
      if (!window.confirm(`Remove ADMIN access from ${targetUser.email}?\n\nThis will immediately revoke their admin privileges.`)) return;
    }

    setUpdatingUserId(targetUser.id);
    try {
      await adminAPI.updateUserRole(targetUser.id, roleUpper);
      toast.success(`${targetUser.email} role set to ${roleUpper}`);
      setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, role: roleUpper } : u));
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update role for ${targetUser.email}`);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handlePlanChange = async (targetUser, newTier) => {
    const tierUpper = newTier.toUpperCase();
    const currentTier = (targetUser.subscription_tier || 'RECON').toUpperCase();
    if (tierUpper === currentTier) return;

    setUpdatingPlanId(targetUser.id);
    try {
      await adminAPI.updateUserPlan(targetUser.id, tierUpper);
      toast.success(`${targetUser.email} plan set to ${tierUpper}`);
      setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, subscription_tier: tierUpper } : u));
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update plan for ${targetUser.email}`);
    } finally {
      setUpdatingPlanId(null);
    }
  };

  const requestToggleActive = (targetUser) => {
    if (targetUser.id === user.id) {
      toast.error(t('admin.users.cannot_deactivate_self', 'You cannot deactivate your own account.'));
      return;
    }
    if (updatingUserId) return;
    const nextActive = targetUser.is_active === false;
    setActiveToggleConfirm({ id: targetUser.id, email: targetUser.email, nextActive });
  };

  const cancelToggleActive = () => {
    if (!updatingUserId) setActiveToggleConfirm(null);
  };

  const confirmToggleActive = async () => {
    if (!activeToggleConfirm || updatingUserId) return;
    const { id, email, nextActive } = activeToggleConfirm;
    const action = nextActive ? 'activate' : 'deactivate';
    setActiveToggleConfirm(null);
    setUpdatingUserId(id);
    try {
      await adminAPI.updateUser(id, { is_active: String(nextActive) });
      toast.success(`${email} ${nextActive ? 'activated' : 'deactivated'}`);
      setUsers(prev => prev.map(u => (u.id === id ? { ...u, is_active: nextActive } : u)));
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} ${email}`);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const loadBookingInquiries = async () => {
    try {
      const res = await api.get('/admin/all-booking-inquiries');
      setBookingInquiries(res.data.inquiries || []);
    } catch (error) {
      console.error('Failed to load booking inquiries:', error);
    }
  };

  const loadContactInquiries = async () => {
    try {
      const res = await adminAPI.getContactInquiries({ limit: 100 });
      setContactInquiries(Array.isArray(res.data) ? res.data : (res.data?.inquiries || []));
    } catch (error) {
      console.error('Failed to load contact inquiries:', error);
    }
  };

  const loadAllListings = async () => {
    try {
      const res = await api.get('/admin/all-listings?limit=100');
      const listings = res.data.listings || [];
      setAllListings(listings);
      setPendingListings(listings.filter(l => l.status === 'READY_FOR_REVIEW'));
    } catch (error) {
      console.error('Failed to load listings:', error);
    }
  };

  const loadOpsSummary = async () => {
    setOpsLoading(true);
    try {
      const res = await adminAPI.getOpsSummary();
      setOpsSummary(res.data);
    } catch (error) {
      console.error('Failed to load ops summary:', error);
    } finally {
      setOpsLoading(false);
    }
  };

  const loadInquiryTraces = async (ownerEmailOverride) => {
    setInquiryTraceLoading(true);
    setInquiryTraceError(null);
    const qaEmail = (ownerEmailOverride || qaOwnerEmail || '').trim();
    try {
      const res = await api.get('/admin/ops/inquiry-trace', {
        params: {
          recent: true,
          limit: 10,
          ...(qaEmail ? { qa_owner_email: qaEmail } : {}),
        },
      });
      setInquiryTraces(res.data.traces || []);
      if (res.data.configured_temp_owner_email) {
        setConfiguredTempOwnerEmail(res.data.configured_temp_owner_email);
        if (!qaOwnerEmail) {
          setQaOwnerEmail(res.data.configured_temp_owner_email);
        }
      }
    } catch (error) {
      console.error('Failed to load inquiry traces:', error);
      setInquiryTraces([]);
      setInquiryTraceError(
        t('admin.inquiry_trace_load_failed', 'Failed to load inquiry routing trace'),
      );
    } finally {
      setInquiryTraceLoading(false);
    }
  };

  const loadInquiryTraceById = async (inquiryId) => {
    const id = String(inquiryId || '').trim();
    if (!id) {
      return;
    }
    setInquiryTraceLoading(true);
    setInquiryTraceError(null);
    try {
      const res = await api.get(`/admin/ops/inquiry-trace/${encodeURIComponent(id)}`);
      const trace = res.data?.trace;
      if (trace) {
        setInquiryTraces([trace]);
      } else {
        setInquiryTraces([]);
        setInquiryTraceError(
          t('admin.bookings.trace_not_found', 'Inquiry trace not found for this booking.'),
        );
      }
    } catch (error) {
      console.error('Failed to load inquiry trace by id:', error);
      setInquiryTraces([]);
      setInquiryTraceError(
        error.response?.status === 404
          ? t('admin.bookings.trace_not_found', 'Inquiry trace not found for this booking.')
          : t('admin.inquiry_trace_load_failed', 'Failed to load inquiry routing trace'),
      );
    } finally {
      setInquiryTraceLoading(false);
    }
  };

  const openInquiryTraceFromBookings = (inquiryId) => {
    const id = getInquiryRecordId({ id: inquiryId });
    if (!id) {
      return;
    }
    setSearchParams({ tab: 'ops', traceInquiryId: id });
    setActiveTab('ops');
  };

  const clearFocusedTrace = () => {
    setSearchParams({ tab: 'ops' });
    loadInquiryTraces();
  };

  const refreshInquiryTraces = () => {
    if (focusedTraceInquiryId) {
      loadInquiryTraceById(focusedTraceInquiryId);
      return;
    }
    loadInquiryTraces();
  };

  const loadQaOwnerListings = async (ownerEmailOverride) => {
    setQaOwnerLoading(true);
    setQaOwnerError(null);
    const email = (ownerEmailOverride || qaOwnerEmail || '').trim();
    try {
      const res = await api.get('/admin/ops/qa-owner-published-listings', {
        params: email ? { owner_email: email } : {},
      });
      setQaOwnerListings(res.data);
      if (res.data.configured_temp_owner_email) {
        setConfiguredTempOwnerEmail(res.data.configured_temp_owner_email);
      }
      if (!qaOwnerEmail && res.data.owner_email) {
        setQaOwnerEmail(res.data.owner_email);
      } else if (!qaOwnerEmail && res.data.configured_temp_owner_email) {
        setQaOwnerEmail(res.data.configured_temp_owner_email);
      }
    } catch (error) {
      console.error('Failed to load QA owner listings:', error);
      setQaOwnerListings(null);
      setQaOwnerError(
        t('admin.qa_owner_listings_load_failed', 'Failed to load QA owner listings'),
      );
    } finally {
      setQaOwnerLoading(false);
    }
  };

  const createQaOwnerListing = async () => {
    const email = (qaOwnerEmail || '').trim();
    if (!email) {
      toast.error(t('admin.qa_owner_email_required', 'Enter the Temp Owner email first'));
      return;
    }
    const confirmed = window.confirm(
      t(
        'admin.qa_create_listing_confirm',
        'Create a new published [QA] test listing owned by this owner? This does not reassign existing listings.',
      ),
    );
    if (!confirmed) {
      return;
    }
    setQaCreateLoading(true);
    try {
      const res = await api.post('/admin/ops/qa-create-owner-listing', {
        owner_email: email,
        confirm: true,
      });
      toast.success(res.data.message || t('admin.qa_create_listing_success', 'QA listing created'));
      await loadQaOwnerListings(email);
    } catch (error) {
      console.error('Failed to create QA listing:', error);
      toast.error(
        error.response?.data?.detail
          || t('admin.qa_create_listing_failed', 'Failed to create QA test listing'),
      );
    } finally {
      setQaCreateLoading(false);
    }
  };

  const openAwaitingReviewQueue = () => {
    setListingStatusFilter('READY_FOR_REVIEW');
    setActiveTab('listings');
    window.scrollTo(0, 0);
    loadAllListings();
  };

  const isPriorityReReview = (listing) =>
    listing?.priority_review === true
    || listing?.priority_reason === 'OWNER_RESUBMISSION'
    || listing?.review_cycle_status === 'RESUBMITTED_FOR_REVIEW';

  const getReceiverQueueTimestamp = (listing) => {
    const candidates = [
      listing?.review_received_at,
      listing?.last_resubmitted_at,
      listing?.resubmitted_for_review_at,
      listing?.submitted_for_review_at,
      listing?.latest_review_round_sent_at,
      listing?.updated_at,
      listing?.created_at,
    ];
    for (const value of candidates) {
      if (!value) continue;
      const ts = new Date(value).getTime();
      if (!Number.isNaN(ts)) return ts;
    }
    return 0;
  };

  const openPriorityReReviewQueue = () => {
    setListingStatusFilter('PRIORITY_RE_REVIEW');
    setActiveTab('listings');
    window.scrollTo(0, 0);
    loadAllListings();
  };

  const toggleRowExpand = (listingId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(listingId)) next.delete(listingId);
      else next.add(listingId);
      return next;
    });
  };

  useEffect(() => {
    if (activeTab === 'ops') {
      loadOpsSummary();
      if (focusedTraceInquiryId) {
        loadInquiryTraceById(focusedTraceInquiryId);
      } else {
        loadInquiryTraces();
      }
      loadQaOwnerListings();
    }
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'bookings') loadBookingInquiries();
    if (activeTab === 'contact') loadContactInquiries();
    if (activeTab === 'reviews') loadPendingReviews();
    if (activeTab === 'listings') loadAllListings();
    if (activeTab === 'settings') {
      loadRefundFees();
      loadMapSettings();
      loadPaymentSettings();
      loadTlaRates();
    }
  }, [activeTab, userSearch, userFilter, focusedTraceInquiryId]);

  useEffect(() => {
    if (
      activeTab !== 'ops'
      || !focusedTraceInquiryId
      || inquiryTraceLoading
      || inquiryTraces.length === 0
    ) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      traceCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeTab, focusedTraceInquiryId, inquiryTraceLoading, inquiryTraces.length]);

  const loadRefundFees = async () => {
    try {
      const res = await api.get('/payments/refund-fees');
      setRefundFees(res.data.config);
    } catch (error) {
      console.error('Failed to load refund fees:', error);
    }
  };

  const loadMapSettings = async () => {
    try {
      const res = await api.get('/admin/map-settings');
      setMapSettings(res.data);
    } catch (error) {
      console.error('Failed to load map settings:', error);
    }
  };

  const loadPaymentSettings = async () => {
    try {
      const res = await api.get('/admin/payment-settings');
      setPaymentSettings(res.data);
    } catch (error) {
      console.error('Failed to load payment settings:', error);
    }
  };

  const loadTlaRates = async () => {
    setLoadingTlaRates(true);
    try {
      const res = await api.get('/tla-rates');
      setTlaRates(res.data.rates || []);
    } catch (error) {
      console.error('Failed to load TLA rates:', error);
      toast.error('Failed to load TLA rates');
    } finally {
      setLoadingTlaRates(false);
    }
  };

  const handleCreateTlaRate = async () => {
    if (!newTlaRate.location_key || !newTlaRate.location_name || !newTlaRate.country || 
        !newTlaRate.daily_single || !newTlaRate.daily_family) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingTlaRate(true);
    try {
      await api.post('/admin/tla-rates', {
        location_key: newTlaRate.location_key,
        location_name: newTlaRate.location_name,
        country: newTlaRate.country,
        daily_single: parseFloat(newTlaRate.daily_single),
        daily_family: parseFloat(newTlaRate.daily_family),
        source: newTlaRate.source || 'DoD Travel Management Office',
        effective_date: newTlaRate.effective_date,
        notes: newTlaRate.notes || ''
      });
      toast.success('TLA rate created successfully');
      setNewTlaRate({
        location_key: '',
        location_name: '',
        country: '',
        daily_single: '',
        daily_family: '',
        source: 'DoD Travel Management Office',
        effective_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      loadTlaRates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create TLA rate');
    } finally {
      setSavingTlaRate(false);
    }
  };

  const handleUpdateTlaRate = async (locationKey) => {
    if (!editingTlaRate) return;
    
    setSavingTlaRate(true);
    try {
      await api.patch(`/admin/tla-rates/${locationKey}`, {
        daily_single: parseFloat(editingTlaRate.daily_single),
        daily_family: parseFloat(editingTlaRate.daily_family),
        source: editingTlaRate.source,
        effective_date: editingTlaRate.effective_date,
        notes: editingTlaRate.notes
      });
      toast.success('TLA rate updated successfully');
      setEditingTlaRate(null);
      loadTlaRates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update TLA rate');
    } finally {
      setSavingTlaRate(false);
    }
  };

  const handleSavePaymentSettings = async () => {
    setSavingPaymentSettings(true);
    try {
      await api.post('/admin/payment-settings', {
        stripe: paymentSettings.stripe,
        paypal: paymentSettings.paypal,
        default_provider: paymentSettings.default_provider
      });
      toast.success('Payment settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment settings');
    } finally {
      setSavingPaymentSettings(false);
    }
  };

  const handleSaveMapSettings = async () => {
    setSavingMapSettings(true);
    try {
      await api.post('/admin/map-settings', mapSettings);
      toast.success('Map settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save map settings');
    } finally {
      setSavingMapSettings(false);
    }
  };

  const handleSaveRefundFees = async () => {
    if (!refundFees) return;
    setSavingFees(true);
    try {
      await api.put('/payments/refund-fees', refundFees);
      toast.success('Refund fee configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save refund fees');
    } finally {
      setSavingFees(false);
    }
  };

  const handleModerateReview = async (reviewId, action) => {
    setModerating(reviewId);
    try {
      await reviewAPI.moderate(reviewId, { action });
      setPendingReviews(prev => prev.filter(r => r.id !== reviewId));
      toast.success(action === 'approve' ? 'Review approved.' : 'Review rejected.');
      const statsRes = await adminAPI.getStats();
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to moderate review');
    } finally {
      setModerating(null);
    }
  };

  const handleApproveListing = async (listingId) => {
    setApprovingListing(listingId);
    try {
      await listingAPI.publish(listingId);
      toast.success('Listing published successfully');
      setAllListings(prev =>
        prev.map(l => l.id === listingId ? { ...l, status: 'PUBLISHED' } : l)
      );
      setPendingListings(prev => prev.filter(l => l.id !== listingId));
      const statsRes = await adminAPI.getStats();
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish listing');
    } finally {
      setApprovingListing(null);
    }
  };

  const handleRejectListing = async () => {
    if (!rejectModal) return;
    const trimmed = rejectReason.trim();
    if (trimmed.length < 10) {
      toast.error('Rejection reason must be at least 10 characters.');
      return;
    }
    setRejecting(true);
    try {
      await adminAPI.rejectListing(rejectModal.listingId, trimmed);
      toast.success('Listing sent back to owner with feedback.');
      setAllListings(prev =>
        prev.map(l => l.id === rejectModal.listingId
          ? { ...l, status: 'DRAFT', rejection_reason: trimmed }
          : l
        )
      );
      setPendingListings(prev => prev.filter(l => l.id !== rejectModal.listingId));
      const statsRes = await adminAPI.getStats();
      setStats(statsRes.data);
      setRejectModal(null);
      setRejectReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject listing.');
    } finally {
      setRejecting(false);
    }
  };

  const handleClaimListing = async (listingId) => {
    setAssigningListing(listingId);
    try {
      const res = await adminAPI.assignReviewer(listingId, user.id);
      const updated = res.data.listing;
      const patch = {
        assigned_reviewer_id: updated.assigned_reviewer_id,
        assigned_reviewer_name: updated.assigned_reviewer_name,
        assigned_reviewer_email: updated.assigned_reviewer_email,
        assignment_status: updated.assignment_status,
        review_assigned_at: updated.review_assigned_at,
      };
      setPendingListings(prev => prev.map(l => l.id === listingId ? { ...l, ...patch } : l));
      setAllListings(prev => prev.map(l => l.id === listingId ? { ...l, ...patch } : l));
      toast.success(t('admin.review_claimed', 'Listing claimed for review.'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim listing.');
    } finally {
      setAssigningListing(null);
    }
  };

  const handleUnassignListing = async (listingId) => {
    setAssigningListing(listingId);
    try {
      await adminAPI.assignReviewer(listingId, null);
      const patch = {
        assigned_reviewer_id: null,
        assigned_reviewer_name: null,
        assigned_reviewer_email: null,
        assignment_status: 'unassigned',
      };
      setPendingListings(prev => prev.map(l => l.id === listingId ? { ...l, ...patch } : l));
      setAllListings(prev => prev.map(l => l.id === listingId ? { ...l, ...patch } : l));
      toast.success(t('admin.review_unassigned', 'Reviewer unassigned.'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unassign reviewer.');
    } finally {
      setAssigningListing(null);
    }
  };

  const handleSentryVerificationTest = async () => {
    setSentryTestLoading(true);
    setSentryTestOutcome(null);
    try {
      const res = await adminAPI.postSentryTest();
      setSentryTestOutcome(resolveSentryTestResult(res.data));
    } catch (error) {
      setSentryTestOutcome(resolveSentryTestError(error?.response?.status));
    } finally {
      setSentryTestLoading(false);
    }
  };

  const handleProcessReminders = async () => {
    setProcessingReminders(true);
    try {
      const res = await api.post('/admin/process-listing-reminders');
      const stats = res.data.stats;
      toast.success(`Processed ${res.data.total_processed} listings. Sent ${stats.reminders_7_days + stats.reminders_3_days + stats.reminders_1_day} reminders, auto-deactivated ${stats.auto_deactivated}`);
    } catch (error) {
      toast.error('Failed to process reminders');
    } finally {
      setProcessingReminders(false);
    }
  };

  if (!isAdmin) return null;

  const formatAge = (isoStr) => {
    if (!isoStr) return null;
    const secs = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (secs < 3600) return `${Math.floor(secs / 60)}m`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
    return `${Math.floor(secs / 86400)}d`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory pt-20" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 py-8 min-w-0">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-lg mb-6">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(217,119,6,0.25),transparent_55%)]" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-military-gold/20 border border-military-gold/40 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-white tracking-tight">{t('admin.title', 'ADMIN DASHBOARD')}</h1>
              <p className="text-slate-300">{t('admin.subtitle', 'Platform management and moderation')}</p>
            </div>
          </div>
          
          <Button
            onClick={handleProcessReminders}
            disabled={processingReminders}
            className="bg-purple-600 hover:bg-purple-500"
          >
            {processingReminders ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-2" />
            )}
            {t('admin.process_reminders', 'Process Listing Reminders')}
          </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {TABS.map(tab => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              onClick={() => { setActiveTab(tab); window.scrollTo(0, 0); }}
              className={activeTab === tab ? 'bg-signal-blue' : 'text-slate-600'}
            >
              {t('admin.tab_' + tab, tab.charAt(0).toUpperCase() + tab.slice(1))}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <div className="flex justify-end mb-4">
              <button
                onClick={fetchData}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                {t('admin.refresh', 'Refresh')}
              </button>
            </div>

            {/* Stats Grid - Row 1 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.users?.total || 0}</p>
                    <p className="text-sm text-slate-600">{t('admin.stat_users', 'Total Users')}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-800 mt-2">
                  {stats?.users?.owners || 0} owners • +{stats?.users?.new_this_week || 0} this week
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <Building2 className="w-8 h-8 text-emerald-400" />
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.listings?.total || 0}</p>
                    <p className="text-sm text-slate-600">{t('admin.stat_listings', 'Total Listings')}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-800 mt-2">{stats?.listings?.published || 0} published</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <Eye className="w-8 h-8 text-cyan-400" />
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.listings?.views_this_week || 0}</p>
                    <p className="text-sm text-slate-600">{t('admin.stat_views', 'Views (7d)')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <Crown className="w-8 h-8 text-military-gold" />
                  <div>
                    <p className="text-2xl font-bold text-slate-800">
                      {(stats?.subscriptions?.basecamp || 0) + (stats?.subscriptions?.command || 0) + (stats?.subscriptions?.fortress || 0)}
                    </p>
                    <p className="text-sm text-slate-600">{t('admin.stat_paid', 'Paid Subscribers')}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-800 mt-2">
                  B:{stats?.subscriptions?.basecamp || 0} C:{stats?.subscriptions?.command || 0} F:{stats?.subscriptions?.fortress || 0}
                </p>
              </div>
            </div>

            {/* Stats Grid - Row 2 */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <Bot className="w-8 h-8 text-signal-blue" />
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.ai_usage?.messages_this_week || 0}</p>
                    <p className="text-sm text-slate-600">{t('admin.stat_ai_messages', 'AI Messages (7d)')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <Heart className="w-8 h-8 text-red-400" />
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.engagement?.total_favorites || 0}</p>
                    <p className="text-sm text-slate-600">{t('admin.stat_favorites', 'Total Favorites')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-8 h-8 text-amber-400" />
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.reviews?.pending || 0}</p>
                    <p className="text-sm text-slate-600">{t('admin.stat_pending_reviews', 'Pending Reviews')}</p>
                  </div>
                </div>
              </div>

              <div
                className="bg-white rounded-xl shadow-sm border border-amber-200 p-4 cursor-pointer hover:bg-amber-50 transition-colors"
                onClick={openAwaitingReviewQueue}
                title="View listings awaiting review"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-8 h-8 text-amber-500" />
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{pendingListings.length}</p>
                    <p className="text-sm text-slate-600">{t('admin.stat_awaiting_review', 'Awaiting Review')}</p>
                  </div>
                </div>
                {pendingListings.length > 0 && (
                  <p className="text-xs text-amber-600 mt-2">{t('admin.stat_listings_ready', 'Listings ready to publish')}</p>
                )}
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <Video className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.walkthroughs?.pending || 0}</p>
                    <p className="text-sm text-slate-600">{t('admin.stat_walkthroughs', 'Pending Walkthroughs')}</p>
                  </div>
                </div>
              </div>
            </div>

            <AdminCapabilityVisibilityPanel t={t} />
          </>
        )}

        {/* Operations Command Center Tab */}
        {activeTab === 'ops' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                <Gauge className="w-5 h-5 text-signal-blue" />
                {t('admin.ops_heading', 'Operations Command Center')}
              </h2>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={loadOpsSummary} disabled={opsLoading}>
                  <RefreshCw className={`w-4 h-4 ${opsLoading ? 'animate-spin' : ''}`} />
                </Button>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={openAwaitingReviewQueue}>
                  {t('admin.ops_go_to_queue', 'View Review Queue')}
                </Button>
              </div>
            </div>

            {/* 1 — Inquiry routing trace */}
            <div
              ref={traceCardRef}
              data-testid={ADMIN_INQUIRY_TRACE_CARD_TEST_ID}
              className="mb-6 bg-white rounded-xl border border-slate-200 shadow-sm p-5 scroll-mt-24"
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-signal-blue" />
                  {t('admin.inquiry_trace_heading', 'Inquiry routing trace')}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={refreshInquiryTraces}
                  disabled={inquiryTraceLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${inquiryTraceLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              {focusedTraceInquiryId && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 p-3 bg-signal-blue/10 border border-signal-blue/20 rounded-lg">
                  <p className="text-sm text-slate-800">
                    {translateTemplate(
                      t(
                        'admin.bookings.trace_focus_banner',
                        'Showing trace for inquiry {ref}',
                      ),
                      { ref: shortId(focusedTraceInquiryId) },
                    )}
                  </p>
                  <Button variant="ghost" size="sm" className="w-full sm:w-auto" onClick={clearFocusedTrace}>
                    {t('admin.bookings.trace_focus_clear', 'Clear focus')}
                  </Button>
                </div>
              )}
              <p className="text-sm text-slate-600 mb-4">
                {t(
                  'admin.inquiry_trace_intro',
                  'Recent guest booking inquiries with routed owner and visibility diagnostics. Admin only.',
                )}
              </p>
              {inquiryTraceError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                  {inquiryTraceError}
                </p>
              )}
              {inquiryTraceLoading && inquiryTraces.length === 0 ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : inquiryTraces.length === 0 ? (
                <p className="text-sm text-slate-500">
                  {t('admin.inquiry_trace_empty', 'No recent booking inquiries to trace.')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="py-2 pr-3 font-medium">{t('admin.inquiry_trace_col_time', 'Created')}</th>
                        <th className="py-2 pr-3 font-medium">{t('admin.inquiry_trace_col_listing', 'Listing')}</th>
                        <th className="py-2 pr-3 font-medium hidden sm:table-cell">{t('admin.inquiry_trace_col_guest', 'Guest')}</th>
                        <th className="py-2 pr-3 font-medium hidden sm:table-cell">{t('admin.inquiry_trace_col_owner', 'Routed owner')}</th>
                        <th className="py-2 pr-3 font-medium hidden md:table-cell">{t('admin.inquiry_trace_col_qa_owner', 'Temp Owner')}</th>
                        <th className="py-2 pr-3 font-medium hidden sm:table-cell">{t('admin.inquiry_trace_col_status', 'Status')}</th>
                        <th className="py-2 font-medium">{t('admin.inquiry_trace_col_health', 'Routing')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inquiryTraces.map((trace) => {
                        const isFocusedTrace = focusedTraceInquiryId
                          && trace.inquiry_id === focusedTraceInquiryId;
                        return (
                        <tr
                          key={trace.inquiry_id}
                          data-testid={isFocusedTrace ? ADMIN_INQUIRY_TRACE_FOCUSED_ROW_TEST_ID : undefined}
                          className={`border-b border-slate-100 last:border-0 ${
                            isFocusedTrace ? 'bg-signal-blue/10 ring-1 ring-inset ring-signal-blue/30' : ''
                          }`}
                        >
                          <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                            {formatTraceTimestamp(trace.created_at, locale)}
                          </td>
                          <td className="py-2 pr-3 text-slate-800">
                            <div className="font-medium truncate max-w-[10rem]" title={trace.listing?.title}>
                              {trace.listing?.title || t('admin.unnamed_listing', 'Unnamed listing')}
                            </div>
                            <div className="text-xs text-slate-500">
                              {trace.inquiry_ref}
                              {trace.listing?.listing_id && (
                                <span>
                                  {' · '}
                                  {String(trace.listing.listing_id).slice(0, 8)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-slate-700 hidden sm:table-cell">{safeAdminEmailRef(trace)}</td>
                          <td className="py-2 pr-3 text-slate-700 hidden sm:table-cell">{safeOwnerEmailRef(trace)}</td>
                          <td className={`py-2 pr-3 text-xs font-medium hidden md:table-cell ${qaOwnerMatchClass(trace)}`}>
                            {qaOwnerMatchLabel(trace, t)}
                            {qaOwnerMismatchExplanation(trace, t) && (
                              <p className="text-[10px] font-normal text-amber-700 mt-1 max-w-[10rem]">
                                {qaOwnerMismatchExplanation(trace, t)}
                              </p>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-slate-700 hidden sm:table-cell">{trace.status || '—'}</td>
                          <td className="py-2">
                            <Badge className={`text-[10px] border ${getRoutingHealthBadgeClass(trace.routing_health)}`}>
                              {getRoutingHealthLabel(trace.routing_health, t)}
                            </Badge>
                            {trace.mismatch_flags?.length > 0 && (
                              <p className="text-[10px] text-amber-700 mt-1 max-w-[12rem]">
                                {summarizeMismatchFlags(trace, t)}
                              </p>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 2 — QA owner listings */}
            <div
              data-testid={ADMIN_QA_OWNER_LISTINGS_CARD_TEST_ID}
              className="mb-6 bg-white rounded-xl border border-slate-200 shadow-sm p-5"
            >
              <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-signal-blue" />
                {t('admin.qa_owner_listings_heading', 'QA owner listings')}
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                {t(
                  'admin.qa_owner_listings_intro',
                  'Verify published guest-visible listings owned by Temp Owner before guest inquiry QA.',
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <Input
                  value={qaOwnerEmail}
                  onChange={(e) => setQaOwnerEmail(e.target.value)}
                  placeholder={t('admin.qa_owner_email_placeholder', 'Temp owner email')}
                  className="flex-1 sm:max-w-xs"
                />
                <Button variant="outline" onClick={() => loadQaOwnerListings()} disabled={qaOwnerLoading} className="shrink-0">
                  {qaOwnerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.qa_owner_check', 'Check listings')}
                </Button>
                <Button
                  onClick={createQaOwnerListing}
                  disabled={qaCreateLoading || !qaOwnerEmail}
                  className="bg-signal-blue hover:bg-signal-blue-hover text-white shrink-0"
                >
                  {qaCreateLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('admin.qa_create_listing', 'Create [QA] test listing')
                  )}
                </Button>
              </div>
              {configuredTempOwnerEmail && (
                <p className="text-xs text-slate-500 mb-3">
                  {t('admin.qa_configured_owner_hint', 'Render TEMP_OWNER_EMAIL:')} {configuredTempOwnerEmail}
                </p>
              )}
              {qaOwnerError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                  {qaOwnerError}
                </p>
              )}
              {qaOwnerListings && (
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                  <p className="text-sm text-slate-800 font-medium">{qaOwnerListings.message}</p>
                  {qaOwnerListings.owner_found ? (
                    <>
                      <p className="text-xs text-slate-600 mt-2">
                        {t('admin.qa_owner_id_label', 'Owner id')}: {qaOwnerListings.owner_id}
                        {' · '}
                        {t('admin.qa_published_count', 'Published')}: {qaOwnerListings.published_count}
                      </p>
                      {qaOwnerListings.published_count === 0 ? (
                        <p className="text-xs text-amber-700 mt-2">
                          {t(
                            'admin.qa_owner_zero_published',
                            'Temp Owner has zero published guest-visible listings. Create a QA listing before guest submit tests.',
                          )}
                        </p>
                      ) : (
                        <ul className="mt-3 space-y-2 text-xs text-slate-700">
                          {qaOwnerListings.published_listings.map((listing) => (
                            <li key={listing.id} className="rounded-md border border-slate-200 bg-white px-3 py-2">
                              <span className="font-medium">{listing.title}</span>
                              {' · '}
                              <span>{listing.listing_ref || listing.id?.slice(0, 8)}</span>
                              {listing.qa_test_listing && (
                                <Badge className="ml-2 text-[10px] bg-violet-100 text-violet-800 border-violet-200">
                                  QA
                                </Badge>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </div>

            {/* 3 — Operational metrics */}
            {opsLoading && !opsSummary ? (
              <div className="mb-6 flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : opsSummary ? (
              <>
                {/* Review Queue */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4" />
                    {t('admin.ops_queue_heading', 'Review Queue')}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div
                      className="bg-white rounded-xl border border-amber-200 shadow-sm p-4 cursor-pointer hover:bg-amber-50 transition-colors"
                      onClick={openAwaitingReviewQueue}
                    >
                      <p className="text-3xl font-bold text-slate-800">{opsSummary.queue.ready_for_review}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_queue_ready', 'Ready for Review')}</p>
                    </div>
                    <div
                      className={`bg-white rounded-xl border shadow-sm p-4 cursor-pointer transition-colors hover:bg-orange-50 ${(opsSummary.queue.priority_re_reviews ?? 0) > 0 ? 'border-orange-400' : 'border-slate-200'}`}
                      onClick={openPriorityReReviewQueue}
                      title={t('admin.ops_queue_priority_title', 'View priority re-review queue')}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <Flag className={`w-4 h-4 ${(opsSummary.queue.priority_re_reviews ?? 0) > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
                        <p className={`text-3xl font-bold ${(opsSummary.queue.priority_re_reviews ?? 0) > 0 ? 'text-orange-600' : 'text-slate-800'}`}>{opsSummary.queue.priority_re_reviews ?? 0}</p>
                      </div>
                      <p className="text-sm text-slate-600">{t('admin.ops_queue_priority', 'Priority Re-reviews')}</p>
                      {(opsSummary.queue.priority_re_reviews ?? 0) > 0 && (
                        <p className="text-xs text-orange-500 mt-1">{t('admin.ops_queue_priority_hint', 'Owner resubmitted after admin notes')}</p>
                      )}
                    </div>
                    <div className={`bg-white rounded-xl border shadow-sm p-4 ${opsSummary.queue.unassigned > 0 ? 'border-amber-300' : 'border-slate-200'}`}>
                      <p className={`text-3xl font-bold ${opsSummary.queue.unassigned > 0 ? 'text-amber-600' : 'text-slate-800'}`}>{opsSummary.queue.unassigned}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_queue_unassigned', 'Unassigned')}</p>
                    </div>
                    <div className={`bg-white rounded-xl border shadow-sm p-4 ${opsSummary.queue.overdue_3d > 0 ? 'border-red-300' : 'border-slate-200'}`}>
                      <p className={`text-3xl font-bold ${opsSummary.queue.overdue_3d > 0 ? 'text-red-600' : 'text-slate-800'}`}>{opsSummary.queue.overdue_3d}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_queue_overdue', 'Overdue (3d+)')}</p>
                      {opsSummary.queue.overdue_3d > 0 && (
                        <p className="text-xs text-red-500 mt-1">{t('admin.ops_overdue_warning', 'Needs attention')}</p>
                      )}
                    </div>
                    <div className={`bg-white rounded-xl border shadow-sm p-4 ${opsSummary.queue.requires_owner_action > 0 ? 'border-amber-300' : 'border-slate-200'}`}>
                      <p className={`text-3xl font-bold ${opsSummary.queue.requires_owner_action > 0 ? 'text-amber-600' : 'text-slate-800'}`}>{opsSummary.queue.requires_owner_action}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_queue_owner_action', 'Owner Action Required')}</p>
                    </div>
                  </div>
                  {opsSummary.queue.by_country && Object.keys(opsSummary.queue.by_country).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                      <span className="text-xs text-slate-500 font-medium">{t('admin.ops_by_country', 'By Country')}:</span>
                      {Object.entries(opsSummary.queue.by_country).map(([cc, cnt]) => (
                        <span key={cc} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full border border-slate-200">
                          {cc.toUpperCase()} {cnt}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pipeline */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    {t('admin.ops_pipeline_heading', 'Pipeline')}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <p className="text-3xl font-bold text-emerald-600">{opsSummary.pipeline.recently_published_7d}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_published_7d', 'Published (7d)')}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <p className="text-3xl font-bold text-slate-800">{opsSummary.pipeline.total_published}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_total_published', 'Total Published')}</p>
                    </div>
                    <div className={`bg-white rounded-xl border shadow-sm p-4 ${opsSummary.pipeline.stale_drafts_14d > 5 ? 'border-amber-200' : 'border-slate-200'}`}>
                      <p className={`text-3xl font-bold ${opsSummary.pipeline.stale_drafts_14d > 5 ? 'text-amber-600' : 'text-slate-800'}`}>{opsSummary.pipeline.stale_drafts_14d}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_stale_drafts', 'Stale Drafts (14d)')}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <p className="text-3xl font-bold text-slate-800">{opsSummary.pipeline.total_draft}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_total_draft', 'Total Drafts')}</p>
                    </div>
                  </div>
                </div>

                {/* Activity 7d */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Timer className="w-4 h-4" />
                    {t('admin.ops_activity_heading', 'Activity (7 days)')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <p className="text-3xl font-bold text-signal-blue">{opsSummary.activity_7d.review_batches_sent}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_batches_sent', 'Review Batches Sent')}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <p className="text-3xl font-bold text-emerald-600">{opsSummary.activity_7d.notes_addressed}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_notes_addressed', 'Notes Addressed')}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <p className="text-3xl font-bold text-slate-800">{opsSummary.activity_7d.resubmissions}</p>
                      <p className="text-sm text-slate-600 mt-1">{t('admin.ops_resubmissions', 'Owner Resubmissions')}</p>
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {/* 4 — Launch Readiness (stuck / pending) */}
            <div className="mb-6 bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  {t('admin.ops_readiness_heading', 'Launch Readiness — Pending Listings')}
                </h3>
                <span className="text-sm text-slate-500">{pendingListings.length} {t('admin.ops_queue_ready', 'Ready for Review')}</span>
              </div>
              {pendingListings.length === 0 ? (
                <p className="p-6 text-slate-500 text-sm">{t('admin.ops_no_pending', 'No listings pending review.')}</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {pendingListings.map(listing => {
                    const score = listingReadinessScore(listing);
                    const barColor = score.pct >= 80 ? 'bg-emerald-500' : score.pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
                    return (
                      <div key={listing.id} className="p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{listing.title || t('admin.unnamed_listing', 'Untitled')}</p>
                          <p className="text-xs text-slate-500">{listing.location_name || listing.location_id || '—'}</p>
                        </div>
                        <div className="w-32 flex-shrink-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-500">{t('admin.ops_readiness_score', 'Score')}</span>
                            <span className={`text-xs font-bold ${score.pct >= 80 ? 'text-emerald-600' : score.pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{score.pct}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${barColor}`} style={{ width: `${score.pct}%` }} />
                          </div>
                          <p className="text-xs text-slate-400 mt-1">{score.passed}/{score.total} {t('admin.ops_readiness_checks', 'checks')}</p>
                        </div>
                        <div className="flex gap-1 flex-wrap justify-end w-44 flex-shrink-0">
                          {score.checks.map(c => (
                            <span
                              key={c.key}
                              title={c.key}
                              className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${c.pass ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}
                            >
                              {c.pass ? '✓' : '✗'}
                            </span>
                          ))}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/owner/listings/${listing.id}/edit`)} className="flex-shrink-0">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 5 — Sentry verification (diagnostic) */}
            <div
              data-testid={ADMIN_SENTRY_VERIFICATION_CARD_TEST_ID}
              className="mb-6 bg-white rounded-xl border border-slate-200 shadow-sm p-5"
            >
              <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-signal-blue" />
                {t('admin.sentry_verify_heading', 'Sentry Verification')}
              </h3>
              <p className="text-sm text-slate-600 mb-2">
                {t('admin.sentry_verify_intro', 'Sends one backend Sentry verification message. No customer data is sent.')}
              </p>
              <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1 mb-4">
                <li>{t('admin.sentry_verify_bullet_gate', 'Requires Render env SENTRY_TEST_EVENTS_ENABLED=true (redeploy after changing).')}</li>
                <li>{t('admin.sentry_verify_bullet_disable', 'Turn the flag back to false after verification and redeploy Render.')}</li>
                <li>{t('admin.sentry_verify_bullet_privacy', 'Does not send inquiry text, emails, tokens, or payment data.')}</li>
              </ul>
              <Button
                type="button"
                data-testid={ADMIN_SENTRY_VERIFICATION_BUTTON_TEST_ID}
                onClick={handleSentryVerificationTest}
                disabled={sentryTestLoading}
                variant="outline"
                className="border-signal-blue text-signal-blue hover:bg-signal-blue/5"
              >
                {sentryTestLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('admin.sentry_verify_sending', 'Sending…')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('admin.sentry_verify_button', 'Send backend Sentry test event')}
                  </>
                )}
              </Button>
              {sentryTestOutcome && (
                <p
                  data-testid={ADMIN_SENTRY_VERIFICATION_RESULT_TEST_ID}
                  className={`mt-4 text-sm rounded-lg border px-3 py-2 ${resultToneClass(sentryTestOutcome.kind)}`}
                >
                  {t(sentryTestOutcome.messageKey)}
                </p>
              )}
            </div>
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                {t('admin.users.heading', 'USER MANAGEMENT')}
              </h2>
              <Button variant="ghost" size="sm" onClick={loadUsers} disabled={usersLoading}>
                <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Filters */}
            <div className="flex gap-3 mb-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder={t('admin.users.search_placeholder', 'Search by name or email...')}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10 bg-white border-slate-300 text-slate-800"
                />
              </div>
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className="bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-700 text-sm"
              >
                <option value="">{t('admin.users.all_roles', 'All Roles')}</option>
                <option value="USER">USER</option>
                <option value="OWNER">OWNER</option>
                <option value="ADMIN">ADMIN</option>
              </select>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">{t('admin.col_user', 'User')}</th>
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">{t('admin.col_role', 'Role')}</th>
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">{t('admin.col_subscription', 'Subscription')}</th>
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">{t('admin.col_status', 'Status')}</th>
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">{t('admin.col_actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-signal-blue" />
                        <p className="text-slate-500 text-sm mt-2">{t('admin.users.loading', 'Loading users…')}</p>
                      </td>
                    </tr>
                  ) : usersError ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center">
                        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                        <p className="text-red-600 text-sm mb-3">{usersError}</p>
                        <Button size="sm" variant="outline" onClick={loadUsers} className="border-slate-300">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          {t('admin.users.retry', 'Retry')}
                        </Button>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center">
                        <Users className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-500 text-sm">
                          {userSearch ? `No users matching "${userSearch}"` : 'No users found'}
                        </p>
                      </td>
                    </tr>
                  ) : users.map(u => {
                    const roleUpper = normalizeRole(u.role);
                    const isSelf = u.id === user.id;
                    const isUpdating = updatingUserId === u.id;
                    const isConfirmingToggle = activeToggleConfirm?.id === u.id;
                    const toggleBusy = !!updatingUserId || (!!activeToggleConfirm && !isConfirmingToggle);
                    const willActivate = u.is_active === false;
                    return (
                      <tr key={u.id} className={`border-b border-slate-100 hover:bg-slate-50 ${isSelf ? 'bg-blue-50/40' : ''}`}>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-slate-800 font-medium text-sm">
                              {u.name || <span className="text-slate-400 italic">{t('admin.users.no_name', 'No name')}</span>}
                              {isSelf && <span className="ml-2 text-xs text-signal-blue font-normal">(you)</span>}
                            </p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <select
                              value={roleUpper}
                              onChange={(e) => handleRoleChange(u, e.target.value)}
                              disabled={isUpdating || isSelf}
                              className={`border rounded px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-signal-blue ${
                                isSelf
                                  ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                                  : roleUpper === 'ADMIN'
                                  ? 'bg-red-50 border-red-200 text-red-700'
                                  : roleUpper === 'OWNER'
                                  ? 'bg-purple-50 border-purple-200 text-purple-700'
                                  : 'bg-slate-50 border-slate-200 text-slate-700'
                              }`}
                              title={isSelf ? 'You cannot change your own role' : 'Change role'}
                            >
                              <option value="USER">USER</option>
                              <option value="OWNER">OWNER</option>
                              <option value="ADMIN">ADMIN</option>
                            </select>
                            {isUpdating && <Loader2 className="w-3.5 h-3.5 animate-spin text-signal-blue" />}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5">
                            <select
                              value={(u.subscription_tier || 'RECON').toUpperCase()}
                              onChange={(e) => handlePlanChange(u, e.target.value)}
                              disabled={updatingPlanId === u.id || updatingUserId === u.id}
                              className="border rounded px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-signal-blue bg-amber-50 border-amber-200 text-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <option value="RECON">RECON</option>
                              <option value="OUTPOST">OUTPOST</option>
                              <option value="BASECAMP">BASECAMP</option>
                              <option value="COMMAND">COMMAND</option>
                              <option value="FORTRESS">FORTRESS</option>
                            </select>
                            {updatingPlanId === u.id && <Loader2 className="w-3 h-3 animate-spin text-signal-blue" />}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {u.is_active !== false ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">{t('admin.status_active', 'Active')}</Badge>
                          ) : (
                            <Badge className="bg-red-50 text-red-700 border-red-200 text-xs">{t('admin.status_inactive', 'Inactive')}</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {isConfirmingToggle ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-xs text-slate-600 max-w-[10rem] truncate" title={u.email}>
                                {willActivate
                                  ? t('admin.users.confirm_activate', 'Activate?')
                                  : t('admin.users.confirm_deactivate', 'Deactivate?')}
                              </span>
                              <Button
                                size="sm"
                                variant={willActivate ? 'default' : 'destructive'}
                                onClick={confirmToggleActive}
                                disabled={isUpdating}
                                className="h-7 px-2 text-xs"
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  t('admin.users.confirm', 'Confirm')
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelToggleActive}
                                disabled={isUpdating}
                                className="h-7 px-2 text-xs"
                              >
                                {t('admin.users.cancel', 'Cancel')}
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => requestToggleActive(u)}
                              disabled={isUpdating || isSelf || toggleBusy}
                              title={
                                isSelf
                                  ? t('admin.users.cannot_deactivate_self_short', 'You cannot deactivate yourself')
                                  : u.is_active !== false
                                  ? t('admin.users.deactivate', 'Deactivate user')
                                  : t('admin.users.activate', 'Activate user')
                              }
                              aria-label={
                                isSelf
                                  ? t('admin.users.cannot_deactivate_self_short', 'You cannot deactivate yourself')
                                  : u.is_active !== false
                                  ? t('admin.users.deactivate', 'Deactivate user')
                                  : t('admin.users.activate', 'Activate user')
                              }
                              className={isSelf ? 'opacity-40 cursor-not-allowed' : u.is_active !== false ? 'text-red-400 hover:text-red-600 hover:bg-red-50' : 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50'}
                            >
                              {isUpdating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : u.is_active !== false ? (
                                <UserX className="h-4 w-4" />
                              ) : (
                                <UserCheck className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {!usersLoading && !usersError && users.length > 0 && (
              <p className="text-xs text-slate-400 mt-3 text-right">{users.length} user{users.length !== 1 ? 's' : ''} shown</p>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-signal-blue" />
                {t('admin.bookings.heading', 'BOOKING INQUIRIES')}
              </h2>
              <Button variant="ghost" size="sm" onClick={loadBookingInquiries}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {bookingInquiries.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-600">{t('admin.bookings.empty', 'No booking inquiries yet')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bookingInquiries.map(inquiry => {
                  const statusBadge = getInquiryStatusBadgeConfig(inquiry.status, t);
                  const inquiryId = getInquiryRecordId(inquiry);
                  return (
                  <div key={inquiry.id} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={statusBadge.badgeClassName}>
                            {statusBadge.label}
                          </Badge>
                          <span className="text-sm text-slate-500">
                            {translateTemplate(
                              t('booking.nights_count', '{count} nights'),
                              { count: inquiry.nights },
                            )}
                          </span>
                        </div>
                        <p className="text-slate-800 font-medium">{inquiry.listing_title}</p>
                        <p className="text-xs text-slate-500">
                          {inquiry.check_in_date} → {inquiry.check_out_date} •{' '}
                          {translateTemplate(
                            t('admin.bookings.guests_count', '{count} guest(s)'),
                            { count: inquiry.guests },
                          )}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {translateTemplate(
                            t('admin.bookings.from_contact', 'From: {name} ({email})'),
                            { name: inquiry.user_name, email: inquiry.user_email },
                          )}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 w-full sm:w-auto sm:items-end">
                        {inquiry.estimated_price && (
                          <div className="text-right">
                            <span className="text-military-gold font-bold">€{inquiry.estimated_price}</span>
                          </div>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
                          data-testid={adminBookingTraceBtnTestId(inquiryId)}
                          onClick={() => openInquiryTraceFromBookings(inquiryId)}
                        >
                          <Activity className="w-4 h-4 mr-1" />
                          {t('admin.bookings.view_trace', 'View trace')}
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                <Mail className="w-5 h-5 text-signal-blue" />
                {t('admin.contact.heading', 'CONTACT SUBMISSIONS')}
              </h2>
              <Button variant="ghost" size="sm" onClick={loadContactInquiries}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {contactInquiries.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-600">{t('admin.contact.empty', 'No contact submissions yet')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contactInquiries.map(item => (
                  <div key={item.id} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge className="bg-signal-blue/10 text-signal-blue border-signal-blue/20">
                        {item.category || t('admin.contact.category_general', 'general')}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {item.created_at ? new Date(item.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <p className="text-slate-800 font-medium">{item.subject}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {translateTemplate(
                        t('admin.contact.from_contact', 'From: {name} ({email})'),
                        { name: item.name, email: item.email },
                      )}
                    </p>
                    <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words">{item.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                {t('admin.reviews.heading', 'PENDING REVIEWS')}
              </h2>
              <Button variant="ghost" size="sm" onClick={loadPendingReviews}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {pendingReviews.length === 0 ? (
              <div className="text-center py-8">
                <Check className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
                <p className="text-slate-600">{t('admin.reviews.empty', 'All reviews have been moderated!')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingReviews.map((review) => (
                  <div key={review.id} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-amber-500/20 text-amber-700 border-amber-300">
                            {review.rating}/5 ⭐
                          </Badge>
                          <span className="text-sm text-slate-500">by {review.user_name}</span>
                        </div>
                        <h3 className="font-medium text-slate-800">{review.title}</h3>
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{review.content}</p>
                        <p className="text-xs text-slate-500 mt-2">For: {review.listing_title}</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleModerateReview(review.id, 'approve')}
                          disabled={moderating === review.id}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white"
                        >
                          {moderating === review.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleModerateReview(review.id, 'reject')}
                          disabled={moderating === review.id}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 min-w-0 overflow-visible">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-400" />
                {t('admin.listings.heading', 'ALL LISTINGS')}
                {pendingListings.length > 0 && (
                  <Badge className="bg-amber-500/20 text-amber-600 border border-amber-300 ml-2">
                    {pendingListings.length} {t('admin.status_awaiting_review', 'awaiting review')}
                  </Badge>
                )}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={listingStatusFilter}
                  onChange={(e) => setListingStatusFilter(e.target.value)}
                  className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-slate-700 text-sm"
                >
                  <option value="">{t('admin.filter_all_statuses', 'All Statuses')}</option>
                  <option value="READY_FOR_REVIEW">{t('admin.status_ready_for_review', 'Ready for Review')}</option>
                  <option value="PRIORITY_RE_REVIEW">{t('admin.ops_queue_priority', 'Priority Re-reviews')}</option>
                  <option value="PUBLISHED">{t('admin.status_published', localized(locale, 'Published', 'Veröffentlicht'))}</option>
                  <option value="DRAFT">{t('admin.status_draft', localized(locale, 'Draft', 'Entwurf'))}</option>
                  <option value="INCOMPLETE">{t('admin.status_incomplete', localized(locale, 'Incomplete', 'Unvollständig'))}</option>
                  <option value="PAUSED">{t('admin.status_paused', localized(locale, 'Paused', 'Pausiert'))}</option>
                </select>
                <select
                  value={listingSort}
                  onChange={(e) => setListingSort(e.target.value)}
                  className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-slate-700 text-sm"
                  aria-label={t('admin.sort_label', 'Sort')}
                >
                  <option value="">{t('admin.sort_default', 'Sort: Default')}</option>
                  <option value="newest">{t('admin.sort_newest', 'Sort: Newest first')}</option>
                  <option value="oldest">{t('admin.sort_oldest', 'Sort: Oldest first')}</option>
                  <option value="waiting">{t('admin.sort_waiting', 'Sort: Longest waiting')}</option>
                </select>
                <Button variant="ghost" size="sm" onClick={loadAllListings}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Card list — actions always inline, no horizontal scroll required */}
            <div
              className="divide-y divide-slate-100 border-t border-slate-100 -mx-6"
              data-testid={ADMIN_LISTINGS_TABLE_TEST_ID}
              role="region"
              aria-label={t('admin.listings_table_region', 'All listings')}
            >
              {(() => {
                const raw = listingStatusFilter === 'READY_FOR_REVIEW'
                  ? pendingListings
                  : listingStatusFilter === 'PRIORITY_RE_REVIEW'
                    ? allListings.filter(l => isPriorityReReview(l) && l.status === 'READY_FOR_REVIEW')
                    : allListings.filter(l => !listingStatusFilter || l.status === listingStatusFilter);

                const defaultSorted = [...raw].sort((a, b) => {
                  if (listingStatusFilter === 'READY_FOR_REVIEW' || listingStatusFilter === 'PRIORITY_RE_REVIEW') {
                    return getReceiverQueueTimestamp(a) - getReceiverQueueTimestamp(b);
                  }
                  const w = l => l.status === 'READY_FOR_REVIEW' && isPriorityReReview(l) ? 0 : l.status === 'READY_FOR_REVIEW' ? 1 : 2;
                  return w(a) - w(b);
                });
                const visibleListings = listingSort
                  ? sortListingsByOrder(defaultSorted, listingSort)
                  : defaultSorted;

                if (visibleListings.length === 0) {
                  return (
                    <div className="py-8 text-center text-slate-500 text-sm px-6">
                      {listingStatusFilter === 'READY_FOR_REVIEW'
                        ? t('admin.listings_empty_awaiting_review', 'No listings awaiting review.')
                        : listingStatusFilter === 'PRIORITY_RE_REVIEW'
                          ? t('admin.listings_empty_priority_rereview', 'No priority re-reviews.')
                          : listingStatusFilter
                            ? t('admin.listings_empty_status_filter', 'No listings found for this status.')
                            : t('admin.listings_empty_all', 'No listings found.')}
                    </div>
                  );
                }

                return visibleListings.flatMap(listing => {
                  const isReady = listing.status === 'READY_FOR_REVIEW';
                  const isPublished = listing.status === 'PUBLISHED';
                  const isMine = listing.assigned_reviewer_id === user?.id;
                  const isAssigned = !!listing.assigned_reviewer_id;
                  const isPriority = isPriorityReReview(listing);
                  const resubAge = isPriority ? formatAge(listing.last_resubmitted_at) : null;
                  const waitAge = formatAge(listing.submitted_for_review_at || listing.resubmitted_for_review_at);
                  const isExpanded = expandedRows.has(listing.id);
                  const photoCount = getListingPhotoCount(listing);
                  const listingRef = buildListingReference(listing);

                  const cards = [(
                    <div
                      key={listing.id}
                      className={`px-6 py-4 transition-colors ${adminListingsRowBg({ isPriority, isReady })}`}
                      data-testid={adminListingsActionsCellTestId(listing.id)}
                    >
                      {/* ── Title row: badges + title + status + price ── */}
                      <div className="flex flex-wrap items-start gap-x-3 gap-y-1 mb-1.5">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            {isPriority && isReady && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded border border-orange-300">
                                <Flag className="w-2.5 h-2.5" />{t('admin.review_priority', 'Priority')}
                              </span>
                            )}
                            {listing.review_round && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded border border-slate-200">
                                R{listing.review_round}
                              </span>
                            )}
                          </div>
                          <p className="text-slate-800 font-semibold text-sm leading-snug">{listing.title || t('admin.unnamed_listing', 'Unnamed listing')}</p>
                          <p className="text-xs text-slate-500">{listing.location_name}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                          <Badge className={`inline-flex whitespace-nowrap text-xs font-medium border px-2 py-0.5 ${
                            isPublished ? 'bg-emerald-500/20 text-emerald-700 border-emerald-300' :
                            listing.auto_deactivated ? 'bg-red-500/20 text-red-700 border-red-300' :
                            isReady ? 'bg-amber-500/20 text-amber-700 border-amber-300' :
                            'bg-slate-500/20 text-slate-600 border-slate-300'
                          }`}>
                            {formatAdminListingStatus(listing, t, locale)}
                          </Badge>
                          {listing.requires_action && (
                            <Badge className="inline-flex whitespace-nowrap bg-yellow-500/20 text-yellow-700 border-yellow-300 text-[10px] px-1.5 py-0">
                              {t('admin.status_action_required', localized(locale, 'Action Required', 'Aktion erforderlich'))}
                            </Badge>
                          )}
                          <span className="text-military-gold font-semibold text-sm tabular-nums whitespace-nowrap">
                            {formatMonthlyPrice(listing.base_amount, locale)}
                          </span>
                          <span
                            className="font-mono text-[10px] tracking-wide text-slate-400 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 whitespace-nowrap select-all"
                            title={t('admin.listing_ref_title', 'Listing reference')}
                            data-testid={`listing-ref-${listing.id}`}
                          >
                            {listingRef}
                          </span>
                        </div>
                      </div>

                      {/* ── Detail row: owner · photos · reviewer · age ── */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs text-slate-500">
                        <span className="truncate max-w-[18rem]">
                          {listing.owner_name || t('admin.owner_unknown', 'Unknown')}
                          {listing.owner_email && <span className="text-slate-400"> · {listing.owner_email}</span>}
                        </span>
                        <span className="whitespace-nowrap tabular-nums flex items-center gap-1.5">
                          {formatPhotoCountLabel(photoCount, locale)}
                          {listing.completion_percent != null && (
                            <span className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 whitespace-nowrap">
                              {listing.completion_percent}% {t('admin.complete', localized(locale, 'complete', 'vollständig'))}
                            </span>
                          )}
                        </span>
                        {isReady && (
                          <span className="flex items-center gap-1">
                            {!isAssigned ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={assigningListing === listing.id}
                                onClick={() => handleClaimListing(listing.id)}
                                className="text-signal-blue hover:bg-signal-blue/10 text-xs px-2 h-6"
                                title={t('admin.review_claim_title', 'Claim this listing for review')}
                              >
                                {assigningListing === listing.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <UserCheck className="w-3 h-3 mr-1" />
                                }
                                {t('admin.review_claim', 'Claim')}
                              </Button>
                            ) : (
                              <span className={`font-medium ${isMine ? 'text-emerald-700' : 'text-slate-700'}`}>
                                {isMine ? `✓ ${t('admin.review_mine', 'Mine')}` : listing.assigned_reviewer_name}
                                {listing.review_assigned_at && (
                                  <span className="font-normal text-slate-400 ml-1">{formatAge(listing.review_assigned_at)} {t('admin.review_ago', 'ago')}</span>
                                )}
                                {!isMine && (
                                  <button
                                    type="button"
                                    disabled={assigningListing === listing.id}
                                    onClick={() => handleClaimListing(listing.id)}
                                    className="ml-1.5 text-signal-blue underline"
                                  >
                                    {t('admin.review_reassign_me', 'Reassign to me')}
                                  </button>
                                )}
                                {isMine && (
                                  <button
                                    type="button"
                                    disabled={assigningListing === listing.id}
                                    onClick={() => handleUnassignListing(listing.id)}
                                    className="ml-1.5 text-slate-400 underline"
                                  >
                                    {t('admin.review_unassign', 'Unassign')}
                                  </button>
                                )}
                              </span>
                            )}
                          </span>
                        )}
                        {!isReady && isAssigned && (
                          <span className="text-slate-500">{listing.assigned_reviewer_name}</span>
                        )}
                        {isReady && isPriority && resubAge && (
                          <span className="text-orange-600 whitespace-nowrap">{resubAge} {t('admin.review_since_resubmit', 'since resubmit')}</span>
                        )}
                        {isReady && !isPriority && waitAge && (
                          <span className="text-amber-600 whitespace-nowrap">{waitAge} {t('admin.review_waiting', 'waiting')}</span>
                        )}
                      </div>

                      {/* ── Actions row — always fully visible, no horizontal scroll ── */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={() =>
                            navigateToAdminListingView(navigate, listing.id, { isPublished })
                          }
                          className="h-7 px-2 text-xs text-signal-blue border-signal-blue/30 hover:bg-signal-blue/5 shrink-0"
                          title={isPublished ? t('admin.view_public', 'View public listing') : t('admin.open_editor', 'Open in editor')}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          {t('admin.action_view', 'View')}
                        </Button>
                        {isReady && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApproveListing(listing.id)}
                              disabled={approvingListing === listing.id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 h-7 shrink-0 whitespace-nowrap"
                              title={t('admin.action_publish_title', 'Approve and publish')}
                            >
                              {approvingListing === listing.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Check className="w-3 h-3 mr-1" />
                              }
                              {t('admin.action_publish', localized(locale, 'Publish', 'Veröffentlichen'))}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setReviewNotesModal(listing)}
                              className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 h-7 px-2 text-xs shrink-0 whitespace-nowrap"
                              title={t('admin.review_notes_title', localized(locale, 'Send review notes to owner', 'Prüfhinweise an Eigentümer senden'))}
                              aria-label={t('admin.action_review_notes', localized(locale, 'Review Notes', 'Prüfhinweise'))}
                              data-testid="review-notes-btn"
                            >
                              <Send className="w-3.5 h-3.5 mr-1" />
                              {t('admin.action_review_notes', localized(locale, 'Review Notes', 'Prüfhinweise'))}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setRejectReason('');
                                setRejectModal({ listingId: listing.id, title: listing.title });
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 h-7 shrink-0 whitespace-nowrap"
                              title={t('admin.action_reject_title', 'Send back to owner with feedback')}
                            >
                              <X className="w-3 h-3 mr-1" />
                              {t('admin.action_reject', localized(locale, 'Reject', 'Ablehnen'))}
                            </Button>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleRowExpand(listing.id)}
                          className="ml-auto flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition-colors"
                          title={t('admin.listing_context_btn', 'Context')}
                          aria-label={isExpanded ? t('admin.listing_collapse', 'Collapse details') : t('admin.listing_context_btn', 'Context')}
                        >
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />
                          }
                          <span className="hidden sm:inline">{t('admin.listing_context_btn', 'Context')}</span>
                        </button>
                      </div>
                    </div>
                  )];

                  if (isExpanded) {
                    cards.push(
                      <div key={`${listing.id}-ctx`} className="bg-slate-50/40 px-6 pb-4 pt-0.5">
                        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">

                          {/* Panel header */}
                          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-2.5">
                            <div className="flex items-center gap-2">
                              <ClipboardCheck className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                {adminCtxLabel(t, locale, 'admin.ctx_panel_title', 'Review Details', 'Prüfdetails', 'Detalles de revisión')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className="font-mono text-[10px] tracking-wide text-slate-400 bg-white border border-slate-200 rounded px-1.5 py-0.5 whitespace-nowrap select-all"
                                title={t('admin.listing_ref_title', 'Listing reference')}
                              >
                                {listingRef}
                              </span>
                              <span className="hidden text-xs text-slate-400 sm:block">
                                {adminCtxLabel(t, locale, 'admin.ctx_panel_subtitle', 'Operational context for this listing', 'Operativer Kontext für dieses Inserat', 'Contexto operativo de este anuncio')}
                              </span>
                            </div>
                          </div>

                          {/* 3-column main content */}
                          <div className="grid min-w-0 grid-cols-1 divide-y divide-slate-100 md:grid-cols-3 md:divide-x md:divide-y-0">

                            {/* Column 1: Location & Eligibility */}
                            <div className="p-5">
                              <p className="mb-3.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                                <MapPin className="h-3.5 w-3.5 shrink-0" />
                                {adminCtxLabel(t, locale, 'admin.ctx_section_country', 'Location', 'Standort', 'Ubicación')}
                              </p>
                              <div className="space-y-3">
                                <div>
                                  <p className="mb-1 text-xs text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_country', 'Country', 'Land', 'País')}</p>
                                  <span className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-1 text-sm font-bold text-slate-700 ring-1 ring-inset ring-slate-200">
                                    {listing.country_code || '—'}
                                  </span>
                                </div>
                                {listing.location_name && (
                                  <div>
                                    <p className="mb-0.5 text-xs text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_location_name', 'Area', 'Ort', 'Zona')}</p>
                                    <p className="text-sm font-semibold text-slate-800 leading-snug">{listing.location_name}</p>
                                  </div>
                                )}
                                <div>
                                  <p className="mb-1 text-xs text-slate-400">{t('admin.ctx_tla_status', 'TLA')}</p>
                                  {listing.tla_eligible === true ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                      <Check className="h-3 w-3" />
                                      {adminCtxLabel(t, locale, 'admin.ctx_tla_yes', 'TLA Eligible', 'TLA-berechtigt', 'Apto para TLA')}
                                    </span>
                                  ) : listing.tla_eligible === false ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-inset ring-slate-200">
                                      <X className="h-3 w-3" />
                                      {adminCtxLabel(t, locale, 'admin.ctx_tla_no', 'Not TLA eligible', 'Nicht TLA-berechtigt', 'No apto para TLA')}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-slate-400">—</span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Column 2: Property Facts */}
                            <div className="p-5">
                              <p className="mb-3.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                                <Building2 className="h-3.5 w-3.5 shrink-0" />
                                {adminCtxLabel(t, locale, 'admin.ctx_section_property', 'Property', 'Objekt', 'Propiedad')}
                              </p>
                              <dl className="grid grid-cols-2 gap-x-6 gap-y-3.5">
                                <div>
                                  <dt className="text-xs text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_type', 'Type', 'Typ', 'Tipo')}</dt>
                                  <dd className="mt-0.5 text-sm font-semibold text-slate-800">{listing.property_type || '—'}</dd>
                                </div>
                                <div>
                                  <dt className="text-xs text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_sqm', 'Size', 'Größe', 'Tamaño')}</dt>
                                  <dd className="mt-0.5 text-sm font-semibold text-slate-800">{listing.sqm ? `${listing.sqm} m²` : '—'}</dd>
                                </div>
                                <div>
                                  <dt className="text-xs text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_bedrooms', 'Bedrooms', 'Schlafzimmer', 'Dormitorios')}</dt>
                                  <dd className="mt-0.5 text-sm font-semibold text-slate-800">{listing.bedrooms ?? '—'}</dd>
                                </div>
                                <div>
                                  <dt className="text-xs text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_bathrooms', 'Bathrooms', 'Badezimmer', 'Baños')}</dt>
                                  <dd className="mt-0.5 text-sm font-semibold text-slate-800">{listing.bathrooms ?? '—'}</dd>
                                </div>
                              </dl>
                            </div>

                            {/* Column 3: Owner / Contact */}
                            <div className="min-w-0 p-5">
                              <p className="mb-3.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                                <Users className="h-3.5 w-3.5 shrink-0" />
                                {adminCtxLabel(t, locale, 'admin.ctx_section_owner', 'Owner', 'Eigentümer', 'Propietario')}
                              </p>
                              <dl className="min-w-0 space-y-3.5">
                                <div>
                                  <dt className="text-xs text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_owner_name_label', 'Name', 'Name', 'Nombre')}</dt>
                                  <dd className="mt-0.5 break-words text-sm font-semibold text-slate-800">{listing.owner_name || t('admin.owner_unknown', 'Unknown')}</dd>
                                </div>
                                <div className="min-w-0">
                                  <dt className="flex items-center gap-1 text-xs text-slate-400">
                                    <Mail className="h-3 w-3 shrink-0" />
                                    {adminCtxLabel(t, locale, 'admin.ctx_email', 'Owner email', 'Eigentümer-E-Mail', 'Email del propietario')}
                                  </dt>
                                  <dd className="mt-0.5 min-w-0">
                                    {listing.owner_email ? (
                                      <a
                                        href={`mailto:${listing.owner_email}`}
                                        className="block break-all text-sm font-medium leading-snug text-slate-700 hover:text-slate-900 hover:underline"
                                        title={listing.owner_email}
                                      >
                                        {listing.owner_email}
                                      </a>
                                    ) : (
                                      <span className="text-sm text-slate-400">—</span>
                                    )}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                          </div>

                          {/* Full-width metadata footer */}
                          <footer className="border-t border-slate-100 bg-slate-50/60 px-5 py-3">
                            <div className="flex flex-wrap items-start gap-x-8 gap-y-2.5">
                              <div className="flex items-start gap-2">
                                <Crown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                                <div>
                                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_section_owner_plan', 'Owner plan', 'Eigentümer-Plan', 'Plan del propietario')}</p>
                                  <p className="text-sm font-semibold text-slate-700">{listing.owner_plan || '—'}</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <Calculator className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                                <div>
                                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_section_tla_location', 'TLA Support', 'TLA-Unterstützung', 'Soporte TLA')}</p>
                                  <p className="text-sm font-semibold text-slate-700">
                                    {listing.tla_eligible === true
                                      ? adminCtxLabel(t, locale, 'admin.ctx_tla_eligible_yes', 'TLA Eligible', 'TLA-berechtigt', 'Apto para TLA')
                                      : listing.tla_eligible === false
                                      ? adminCtxLabel(t, locale, 'admin.ctx_tla_eligible_no', 'Not TLA eligible', 'Nicht TLA-berechtigt', 'No apto para TLA')
                                      : '—'
                                    }
                                  </p>
                                </div>
                              </div>
                              {listing.nearby_bases?.length > 0 && (
                                <div className="flex min-w-0 flex-1 items-start gap-2">
                                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{adminCtxLabel(t, locale, 'admin.ctx_nearby_bases', 'Nearby bases', 'Nahegelegene Stützpunkte', 'Bases cercanas')}</p>
                                    <p className="break-words text-sm text-slate-700 leading-snug">{listing.nearby_bases.join(', ')}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </footer>
                        </div>
                      </div>
                    );
                  }
                  return cards;
                });
              })()}
            </div>
          </div>
        )}

        {/* Settings Tab - Refund Fee Configuration */}
        {activeTab === 'settings' && (
          <>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              {t('admin.settings.refund_heading', 'REFUND FEE CONFIGURATION')}
            </h2>
            <p className="text-slate-600 text-sm mb-6">
              Configure how refund fees are split between the platform and property owners when guests cancel bookings.
            </p>

            {refundFees ? (
              <div className="space-y-6">
                {/* Fee Percentages */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <h3 className="text-slate-700 font-medium mb-3">Platform Fee</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-slate-800 block mb-1">Fee Percentage (%)</label>
                        <Input
                          type="number"
                          min="0"
                          max="50"
                          step="0.5"
                          value={refundFees.platform_fee_percent}
                          onChange={(e) => setRefundFees({...refundFees, platform_fee_percent: parseFloat(e.target.value) || 0})}
                          className="bg-white border-slate-300"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-800 block mb-1">Minimum Fee (€)</label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={refundFees.min_platform_fee}
                          onChange={(e) => setRefundFees({...refundFees, min_platform_fee: parseFloat(e.target.value) || 0})}
                          className="bg-white border-slate-300"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <h3 className="text-slate-700 font-medium mb-3">Owner Fee</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-slate-800 block mb-1">Fee Percentage (%)</label>
                        <Input
                          type="number"
                          min="0"
                          max="50"
                          step="0.5"
                          value={refundFees.owner_fee_percent}
                          onChange={(e) => setRefundFees({...refundFees, owner_fee_percent: parseFloat(e.target.value) || 0})}
                          className="bg-white border-slate-300"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-800 block mb-1">Minimum Fee (€)</label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={refundFees.min_owner_fee}
                          onChange={(e) => setRefundFees({...refundFees, min_owner_fee: parseFloat(e.target.value) || 0})}
                          className="bg-white border-slate-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Refund Windows */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h3 className="text-slate-700 font-medium mb-3">Refund Windows</h3>
                  <p className="text-xs text-slate-500 mb-4">{t('admin.settings.refund_windows_hint', 'Configure how many days before check-in different refund policies apply')}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-slate-800 block mb-1">Full Refund Window (days)</label>
                      <Input
                        type="number"
                        min="0"
                        value={refundFees.full_refund_window_days}
                        onChange={(e) => setRefundFees({...refundFees, full_refund_window_days: parseInt(e.target.value) || 0})}
                        className="bg-white border-slate-300"
                      />
                      <p className="text-xs text-emerald-600 mt-1">100% refund (minus fees)</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-800 block mb-1">Partial Refund Window (days)</label>
                      <Input
                        type="number"
                        min="0"
                        value={refundFees.partial_refund_window_days}
                        onChange={(e) => setRefundFees({...refundFees, partial_refund_window_days: parseInt(e.target.value) || 0})}
                        className="bg-white border-slate-300"
                      />
                      <p className="text-xs text-amber-600 mt-1">50% refund</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-800 block mb-1">No Refund Window (days)</label>
                      <Input
                        type="number"
                        min="0"
                        value={refundFees.no_refund_window_days}
                        onChange={(e) => setRefundFees({...refundFees, no_refund_window_days: parseInt(e.target.value) || 0})}
                        className="bg-white border-slate-300"
                      />
                      <p className="text-xs text-red-600 mt-1">{t('admin.settings.no_refund', 'No refund')}</p>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                  <h4 className="text-purple-700 font-medium mb-2">Example Calculation</h4>
                  <p className="text-sm text-slate-600">
                    For a €200 deposit cancelled 10+ days before check-in (full refund):
                  </p>
                  <ul className="text-sm text-slate-600 mt-2 space-y-1">
                    <li>• Platform receives: €{Math.max(refundFees.min_platform_fee, 200 * (refundFees.platform_fee_percent / 100)).toFixed(2)}</li>
                    <li>• Owner receives: €{Math.max(refundFees.min_owner_fee, 200 * (refundFees.owner_fee_percent / 100)).toFixed(2)}</li>
                    <li>• Guest receives: €{(200 - Math.max(refundFees.min_platform_fee, 200 * (refundFees.platform_fee_percent / 100)) - Math.max(refundFees.min_owner_fee, 200 * (refundFees.owner_fee_percent / 100))).toFixed(2)}</li>
                  </ul>
                </div>

                <div className="flex justify-end">
                  <Button 
                    onClick={handleSaveRefundFees}
                    disabled={savingFees}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                  >
                    {savingFees ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Save Configuration
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
              </div>
            )}
          </div>

          {/* Map Settings Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mt-6">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-400" />
              {t('admin.settings.map_heading', 'MAP SETTINGS')}
            </h2>
            <p className="text-slate-600 text-sm mb-6">
              Control the visibility of map features. Changes affect all users except admins (who always see all features).
            </p>

            <div className="space-y-6">
              {/* Amenities Toggle */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-slate-800 font-medium">Show Amenities on Map</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      When enabled, users can see schools, medical facilities, and commissaries on the 3D globe map.
                    </p>
                  </div>
                  <button
                    onClick={() => setMapSettings({...mapSettings, amenities_enabled: !mapSettings.amenities_enabled})}
                    className={`w-12 h-6 rounded-full transition-colors ${mapSettings.amenities_enabled ? 'bg-signal-blue' : 'bg-slate-400'}`}
                    data-testid="admin-amenities-toggle"
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${mapSettings.amenities_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                
                <div className={`mt-4 p-3 rounded-lg ${mapSettings.amenities_enabled ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <p className={`text-sm ${mapSettings.amenities_enabled ? 'text-green-700' : 'text-amber-700'}`}>
                    {mapSettings.amenities_enabled 
                      ? '✓ Amenities are visible to all users on the map'
                      : '⚠ Amenities are hidden from regular users (admins can still see them)'
                    }
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveMapSettings}
                  disabled={savingMapSettings}
                  className="bg-signal-blue hover:bg-signal-blue-hover"
                >
                  {savingMapSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Map Settings
                </Button>
              </div>
            </div>
          </div>

          {/* Payment Settings Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mt-6" data-testid="payment-settings-section">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              {t('admin.settings.payment_heading', 'PAYMENT SETTINGS')}
            </h2>
            <p className="text-slate-600 text-sm mb-6">
              Configure payment providers and control which payment methods are available to users.
            </p>

            {!arePaymentsEnabled() && (
              <div
                className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                data-testid="admin-payments-launch-banner"
              >
                {t(
                  'admin.settings.payments_launch_banner',
                  'Payment provider toggles are inactive until launch. Set PAYMENTS_ENABLED on the backend and REACT_APP_PAYMENTS_ENABLED on the frontend when checkout is ready.',
                )}
              </div>
            )}

            {paymentSettings ? (
              <div className="space-y-6">
                {/* Stripe Settings */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-slate-800 font-medium">Stripe (Credit/Debit Cards)</h3>
                        <p className="text-xs text-slate-500">
                          {paymentSettings.stripe?.configured 
                            ? `Configured • ${paymentSettings.stripe?.test_mode ? 'Test Mode' : 'Live Mode'}`
                            : 'Not Configured'
                          }
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setPaymentSettings({
                        ...paymentSettings, 
                        stripe: {...paymentSettings.stripe, enabled: !paymentSettings.stripe?.enabled}
                      })}
                      className={`w-12 h-6 rounded-full transition-colors ${paymentSettings.stripe?.enabled ? 'bg-emerald-500' : 'bg-slate-400'}`}
                      data-testid="stripe-toggle"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${paymentSettings.stripe?.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  
                  {paymentSettings.stripe?.enabled && (
                    <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-200">
                      <div>
                        <label className="text-xs text-slate-600 block mb-1">Display Name</label>
                        <input
                          type="text"
                          value={paymentSettings.stripe?.display_name || ''}
                          onChange={(e) => setPaymentSettings({
                            ...paymentSettings,
                            stripe: {...paymentSettings.stripe, display_name: e.target.value}
                          })}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-800"
                          placeholder="Credit/Debit Card"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600 block mb-1">Description</label>
                        <input
                          type="text"
                          value={paymentSettings.stripe?.description || ''}
                          onChange={(e) => setPaymentSettings({
                            ...paymentSettings,
                            stripe: {...paymentSettings.stripe, description: e.target.value}
                          })}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-800"
                          placeholder="Pay securely with Visa, Mastercard..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* PayPal Settings */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-slate-800 font-medium">PayPal</h3>
                        <p className="text-xs text-slate-500">
                          {paymentSettings.paypal?.configured 
                            ? `Configured • ${paymentSettings.paypal?.test_mode ? 'Sandbox Mode' : 'Live Mode'}`
                            : 'Not Configured - Add credentials in .env'
                          }
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setPaymentSettings({
                        ...paymentSettings, 
                        paypal: {...paymentSettings.paypal, enabled: !paymentSettings.paypal?.enabled}
                      })}
                      disabled={!paymentSettings.paypal?.configured}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        !paymentSettings.paypal?.configured 
                          ? 'bg-slate-300 cursor-not-allowed'
                          : paymentSettings.paypal?.enabled ? 'bg-emerald-500' : 'bg-slate-400'
                      }`}
                      data-testid="paypal-toggle"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${paymentSettings.paypal?.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  
                  {paymentSettings.paypal?.enabled && paymentSettings.paypal?.configured && (
                    <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-200">
                      <div>
                        <label className="text-xs text-slate-600 block mb-1">Display Name</label>
                        <input
                          type="text"
                          value={paymentSettings.paypal?.display_name || ''}
                          onChange={(e) => setPaymentSettings({
                            ...paymentSettings,
                            paypal: {...paymentSettings.paypal, display_name: e.target.value}
                          })}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-800"
                          placeholder="PayPal"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600 block mb-1">Description</label>
                        <input
                          type="text"
                          value={paymentSettings.paypal?.description || ''}
                          onChange={(e) => setPaymentSettings({
                            ...paymentSettings,
                            paypal: {...paymentSettings.paypal, description: e.target.value}
                          })}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-800"
                          placeholder="Pay with your PayPal account..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Default Provider */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h3 className="text-slate-800 font-medium mb-3">Default Payment Method</h3>
                  <p className="text-xs text-slate-500 mb-4">
                    This payment method will be pre-selected when users make a payment.
                  </p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setPaymentSettings({...paymentSettings, default_provider: 'stripe'})}
                      disabled={!paymentSettings.stripe?.enabled}
                      className={`flex-1 py-3 rounded-lg border-2 transition-all ${
                        paymentSettings.default_provider === 'stripe'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                      } ${!paymentSettings.stripe?.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <CreditCard className={`w-5 h-5 mx-auto mb-1 ${paymentSettings.default_provider === 'stripe' ? 'text-emerald-600' : 'text-slate-400'}`} />
                      <span className={`text-sm font-medium ${paymentSettings.default_provider === 'stripe' ? 'text-emerald-700' : 'text-slate-600'}`}>
                        Stripe
                      </span>
                    </button>
                    <button
                      onClick={() => setPaymentSettings({...paymentSettings, default_provider: 'paypal'})}
                      disabled={!paymentSettings.paypal?.enabled || !paymentSettings.paypal?.configured}
                      className={`flex-1 py-3 rounded-lg border-2 transition-all ${
                        paymentSettings.default_provider === 'paypal'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                      } ${!paymentSettings.paypal?.enabled || !paymentSettings.paypal?.configured ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Wallet className={`w-5 h-5 mx-auto mb-1 ${paymentSettings.default_provider === 'paypal' ? 'text-emerald-600' : 'text-slate-400'}`} />
                      <span className={`text-sm font-medium ${paymentSettings.default_provider === 'paypal' ? 'text-emerald-700' : 'text-slate-600'}`}>
                        PayPal
                      </span>
                    </button>
                  </div>
                </div>

                {/* Status Summary */}
                <div className={`p-4 rounded-lg ${
                  paymentSettings.stripe?.enabled || paymentSettings.paypal?.enabled
                    ? 'bg-emerald-50 border border-emerald-200'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <p className={`text-sm ${
                    paymentSettings.stripe?.enabled || paymentSettings.paypal?.enabled
                      ? 'text-emerald-700'
                      : 'text-red-700'
                  }`}>
                    {paymentSettings.stripe?.enabled && paymentSettings.paypal?.enabled
                      ? '✓ Both Stripe and PayPal are enabled. Users can choose their preferred payment method.'
                      : paymentSettings.stripe?.enabled
                      ? '✓ Stripe is enabled. Users will pay with credit/debit cards only.'
                      : paymentSettings.paypal?.enabled
                      ? '✓ PayPal is enabled. Users will pay with PayPal only.'
                      : '⚠ No payment methods enabled! Users will not be able to make payments.'
                    }
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button 
                    onClick={handleSavePaymentSettings}
                    disabled={savingPaymentSettings}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="save-payment-settings"
                  >
                    {savingPaymentSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Save Payment Settings
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
              </div>
            )}
          </div>

          {/* TLA Rate Management Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mt-6" data-testid="tla-rates-section">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-military-gold" />
              {t('admin.settings.tla_heading', 'TLA RATE MANAGEMENT')}
            </h2>
            <p className="text-slate-600 text-sm mb-6">
              Manage Temporary Lodging Allowance (TLA) per diem rates by location. These rates are used in the TLA Calculator for military families.
            </p>

            {/* Add New Rate Form */}
            <div className="bg-gradient-to-r from-military-gold/5 to-military-gold/10 rounded-lg p-4 border border-military-gold/20 mb-6">
              <h3 className="text-slate-800 font-medium mb-4 flex items-center gap-2">
                <Plus className="w-4 h-4 text-military-gold" />
                Add New TLA Rate
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Location Key *</label>
                  <Input
                    type="text"
                    value={newTlaRate.location_key}
                    onChange={(e) => setNewTlaRate({...newTlaRate, location_key: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                    placeholder="e.g., ramstein_kaiserslautern"
                    className="bg-white border-slate-300 text-slate-800"
                    data-testid="new-tla-location-key"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Location Name *</label>
                  <Input
                    type="text"
                    value={newTlaRate.location_name}
                    onChange={(e) => setNewTlaRate({...newTlaRate, location_name: e.target.value})}
                    placeholder="e.g., Ramstein / Kaiserslautern"
                    className="bg-white border-slate-300 text-slate-800"
                    data-testid="new-tla-location-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Country *</label>
                  <Input
                    type="text"
                    value={newTlaRate.country}
                    onChange={(e) => setNewTlaRate({...newTlaRate, country: e.target.value})}
                    placeholder="e.g., Germany"
                    className="bg-white border-slate-300 text-slate-800"
                    data-testid="new-tla-country"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Daily Rate (Single) € *</label>
                  <Input
                    type="number"
                    value={newTlaRate.daily_single}
                    onChange={(e) => setNewTlaRate({...newTlaRate, daily_single: e.target.value})}
                    placeholder="e.g., 150"
                    className="bg-white border-slate-300 text-slate-800"
                    data-testid="new-tla-daily-single"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Daily Rate (Family) € *</label>
                  <Input
                    type="number"
                    value={newTlaRate.daily_family}
                    onChange={(e) => setNewTlaRate({...newTlaRate, daily_family: e.target.value})}
                    placeholder="e.g., 220"
                    className="bg-white border-slate-300 text-slate-800"
                    data-testid="new-tla-daily-family"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Effective Date</label>
                  <Input
                    type="date"
                    value={newTlaRate.effective_date}
                    onChange={(e) => setNewTlaRate({...newTlaRate, effective_date: e.target.value})}
                    className="bg-white border-slate-300 text-slate-800"
                    data-testid="new-tla-effective-date"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Source</label>
                  <Input
                    type="text"
                    value={newTlaRate.source}
                    onChange={(e) => setNewTlaRate({...newTlaRate, source: e.target.value})}
                    placeholder="DoD Travel Management Office"
                    className="bg-white border-slate-300 text-slate-800"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Notes</label>
                  <Input
                    type="text"
                    value={newTlaRate.notes}
                    onChange={(e) => setNewTlaRate({...newTlaRate, notes: e.target.value})}
                    placeholder="Optional notes..."
                    className="bg-white border-slate-300 text-slate-800"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleCreateTlaRate}
                  disabled={savingTlaRate}
                  className="bg-military-gold hover:bg-military-gold/90 text-slate-900"
                  data-testid="create-tla-rate-btn"
                >
                  {savingTlaRate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Rate
                </Button>
              </div>
            </div>

            {/* Existing Rates Table */}
            <div className="bg-slate-50 rounded-lg border border-slate-200">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-slate-800 font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-signal-blue" />
                  Current TLA Rates ({tlaRates.length})
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadTlaRates}
                  disabled={loadingTlaRates}
                >
                  <RefreshCw className={`w-4 h-4 ${loadingTlaRates ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {loadingTlaRates ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-military-gold animate-spin" />
                </div>
              ) : tlaRates.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Calculator className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p>No TLA rates configured yet</p>
                  <p className="text-sm">{t('admin.settings.tla_empty_hint', 'Add your first rate above')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left text-xs font-medium text-slate-600 px-4 py-3">Location</th>
                        <th className="text-left text-xs font-medium text-slate-600 px-4 py-3">Country</th>
                        <th className="text-right text-xs font-medium text-slate-600 px-4 py-3">Single €</th>
                        <th className="text-right text-xs font-medium text-slate-600 px-4 py-3">Family €</th>
                        <th className="text-left text-xs font-medium text-slate-600 px-4 py-3">Effective</th>
                        <th className="text-center text-xs font-medium text-slate-600 px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {tlaRates.map((rate) => (
                        <tr key={rate.location_key} className="hover:bg-slate-50/50" data-testid={`tla-rate-row-${rate.location_key}`}>
                          {editingTlaRate?.location_key === rate.location_key ? (
                            <>
                              <td className="px-4 py-3">
                                <span className="text-sm text-slate-800 font-medium">{rate.location_name}</span>
                                <br />
                                <span className="text-xs text-slate-400">{rate.location_key}</span>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700">{rate.country}</td>
                              <td className="px-4 py-3">
                                <Input
                                  type="number"
                                  value={editingTlaRate.daily_single}
                                  onChange={(e) => setEditingTlaRate({...editingTlaRate, daily_single: e.target.value})}
                                  className="w-20 text-right bg-white"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <Input
                                  type="number"
                                  value={editingTlaRate.daily_family}
                                  onChange={(e) => setEditingTlaRate({...editingTlaRate, daily_family: e.target.value})}
                                  className="w-20 text-right bg-white"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <Input
                                  type="date"
                                  value={editingTlaRate.effective_date?.split('T')[0] || ''}
                                  onChange={(e) => setEditingTlaRate({...editingTlaRate, effective_date: e.target.value})}
                                  className="w-36 bg-white"
                                />
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateTlaRate(rate.location_key)}
                                    disabled={savingTlaRate}
                                    className="bg-emerald-500 hover:bg-emerald-600"
                                  >
                                    {savingTlaRate ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingTlaRate(null)}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3">
                                <span className="text-sm text-slate-800 font-medium">{rate.location_name}</span>
                                <br />
                                <span className="text-xs text-slate-400">{rate.location_key}</span>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700">{rate.country}</td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-sm font-medium text-slate-800">€{rate.daily_single}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-sm font-medium text-military-gold">€{rate.daily_family}</span>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600">
                                {rate.effective_date ? new Date(rate.effective_date).toLocaleDateString() : '-'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingTlaRate({
                                    location_key: rate.location_key,
                                    daily_single: rate.daily_single,
                                    daily_family: rate.daily_family,
                                    effective_date: rate.effective_date,
                                    source: rate.source,
                                    notes: rate.notes
                                  })}
                                  className="text-signal-blue hover:text-signal-blue-hover"
                                  data-testid={`edit-tla-rate-${rate.location_key}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">About TLA Rates</p>
                  <p className="mt-1">{t('admin.settings.tla_about_body', 'These rates are based on DoD per diem rates and are used in the TLA Calculator to help military families estimate their housing allowances. Update rates when new per diem schedules are released (typically annually).')}</p>
                </div>
              </div>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Reject listing modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-6">
            <h3 className="font-heading text-lg font-bold text-slate-800 mb-1">{t('admin.modal.send_back_title', 'Send Back to Owner')}</h3>
            <p className="text-sm text-slate-600 mb-4 truncate" title={rejectModal.title}>
              {rejectModal.title}
            </p>

            <label className="text-xs font-medium text-slate-700 block mb-1">
              Feedback for owner <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">Min 10 characters. Owner will see this on their listing.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Photos are missing exterior view. Please add at least one exterior photo and resubmit."
              rows={4}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            <p className={`text-xs mt-1 mb-4 ${rejectReason.trim().length < 10 ? 'text-slate-400' : 'text-emerald-600'}`}>
              {rejectReason.trim().length} / 10 minimum
            </p>

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => { setRejectModal(null); setRejectReason(''); }}
                disabled={rejecting}
                className="text-slate-600"
              >
                {t('admin.modal.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleRejectListing}
                disabled={rejecting || rejectReason.trim().length < 10}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {rejecting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('admin.modal.sending', 'Sending…')}</>
                  : <><X className="w-4 h-4 mr-2" />{t('admin.modal.send_back', 'Send Back')}</>
                }
              </Button>
            </div>
          </div>
        </div>
      )}

      <ListingReviewBatchModal
        listing={reviewNotesModal}
        open={!!reviewNotesModal}
        onClose={() => setReviewNotesModal(null)}
        onSent={() => {
          const sentId = reviewNotesModal?.id;
          setReviewNotesModal(null);
          setPendingListings(prev => prev.filter(l => l.id !== sentId));
          setAllListings(prev => prev.map(l => l.id === sentId
            ? { ...l, status: 'DRAFT', priority_review: false, priority_reason: null, requires_action: true }
            : l
          ));
        }}
      />
    </div>
  );
}
